import { createContext, useContext, useMemo } from "react";
import { useAccountScopedFetch } from "../../lib/api";
import {
  DataPlaneClient,
  StreamsApi,
  StreamsDataApi,
  StreamsDataService,
  StreamsService,
} from "decodable-sdk-js/v1alpha2";
import { useDataPlaneClient } from "../../hooks/useDataPlaneClient";

export const StreamsApiContext = createContext<StreamsApi>(null);

// Provides an account-scoped StreamsApi implementation.
// In tests, use StreamsApiContext.Provider directly
// to inject mock services into containers or components.
export function StreamsApiProvider({ children = null }) {
  const fetch = useAccountScopedFetch();
  const service = useMemo(() => fetch && new StreamsService(fetch), [fetch]);
  return (
    <StreamsApiContext.Provider value={service}>
      <StreamsDataApiProvider>{children}</StreamsDataApiProvider>
    </StreamsApiContext.Provider>
  );
}

export function useStreamsApi(): StreamsApi | null {
  return useContext(StreamsApiContext);
}

export const StreamsDataApiContext =
  createContext<Promise<StreamsDataApi>>(null);

export function StreamsDataApiProvider({ children }) {
  const dataPlaneClient = useDataPlaneClient();

  const service = useMemo(async () => {
    return new StreamsDataService({
      client: await dataPlaneClient,
    });
  }, [dataPlaneClient]);

  return (
    <StreamsDataApiContext.Provider value={service}>
      {children}
    </StreamsDataApiContext.Provider>
  );
}

export function useStreamsDataApi(): Promise<StreamsDataApi> | null {
  return useContext(StreamsDataApiContext);
}
